import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { i18n } from '@/shared/i18n';

import { tiptapToMarkdown } from './tiptapToMarkdown';
import { formatSectionValue } from './formatSectionValue';

/**
 * Minimal structural shape needed to serialize a brief to Markdown. Both the
 * `Brief` domain type (`model/types`) and the SDK `BriefResponseDto` satisfy
 * it, which is exactly what the two call sites (row menu + detail header)
 * already pass.
 */
interface BriefMarkdownInput {
  title: string;
  sections: {
    name: string;
    position: number;
    content?: unknown;
    type?: string | null;
    config?: Record<string, unknown> | null;
    value?: unknown;
    retired?: boolean;
  }[];
}

/**
 * Serializes a brief to a single Markdown document: an `# {title}` H1 over each
 * section rendered as `## {section}` + its content, ordered by `position` to match the
 * detail view. `type == null`/`'text'` sections render their TipTap `content` (unchanged
 * from before typed fields existed); every other type renders `formatSectionValue`'s display
 * form instead, falling back to its prose when it holds no value. A retired section is still
 * included, labeled `(Retired)` after its heading.
 */
export function briefToMarkdown(brief: BriefMarkdownInput): string {
  const body = [...brief.sections]
    .sort((a, b) => a.position - b.position)
    .map((s) => {
      // Point-of-use `i18n.t()`, not module scope — a module-scope call would freeze the
      // string at import time in whatever locale loaded first.
      const heading = s.retired
        ? `## ${s.name} (${i18n.t('projects:projectBriefDetail.briefSectionShell.retired')})`
        : `## ${s.name}`;
      const isProse = s.type == null || s.type === 'text';
      const formatted = isProse ? '' : formatSectionValue(s.type ?? null, s.config ?? {}, s.value);
      const prose = tiptapToMarkdown(s.content as Record<string, unknown> | null);

      // A typed section can still be carrying prose and no value: agent generation writes
      // `content` only, and brief creation stamps the template's type on afterwards. Rendering
      // the formatted empty string there loses the only text the section has, so it falls back
      // to the prose. The same fallback lives in the server-side PDF export
      // (`apps/workspace-api/src/briefs/export/brief-export.service.ts`); the two exports run in
      // different runtimes and share no module, so they are kept parallel by hand - same
      // arrangement as the two copies of `formatSectionValue`.
      const body = isProse || formatted === '' ? prose : formatted;
      return `${heading}\n\n${body}`;
    })
    .join('\n');
  return `# ${brief.title}\n\n${body}`;
}

/**
 * Writes the brief's Markdown to a `text/markdown` Blob and triggers a browser
 * download named `{briefTitle}.md`, delegating the Blob → object-URL →
 * anchor-click → revoke mechanic to the shared `downloadFile` sink. The blob
 * path is fully synchronous, so the returned promise resolves immediately —
 * fire-and-forget keeps this a `void` API for its sync call sites.
 */
export function downloadBriefMarkdown(brief: BriefMarkdownInput): void {
  const blob = new Blob([briefToMarkdown(brief)], { type: 'text/markdown' });
  // eslint-disable-next-line i18next/no-literal-string -- download filename slug, sanitized for the file system
  const fileName = `${sanitizeDownloadFileName(brief.title, 'brief')}.md`;
  void downloadFile({ blob, fileName });
}
